import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('expedify_token'));

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('expedify_token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  /** Take a TokenResponse and become signed in. Every route that ends in a session says
   *  the same three things, and the continue page ends in one without a password having
   *  been typed into a login form. */
  const adoptSession = ({ access_token, user: userData }) => {
    localStorage.setItem('expedify_token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, {
      email,
      password
    });

    return adoptSession(response.data);
  };

  const register = async (userData) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, userData);

    const { access_token, user: newUser } = response.data;
    localStorage.setItem('expedify_token', access_token);
    setToken(access_token);
    setUser(newUser);
    return newUser;
  };

  /* ---- support sessions: an admin working inside a partner's portal ----
   *
   * The admin's own token is parked under a SECOND key rather than thrown away, which is the
   * whole reason leaving a session is instant and does not need a fresh sign-in. Both keys are
   * cleared on logout, so a stale admin token can never outlive the session that made it.
   *
   * The banner reads `user.impersonated_by` — reported by /auth/me from the token itself, not
   * held here — so a refresh mid-session still knows it is inside one. React state alone would
   * forget on reload and leave an admin quietly acting as somebody else with nothing on screen
   * to say so.
   */
  const startSupportSession = async (partnerId) => {
    const response = await axios.post(
      `${API_URL}/api/admin/partners/${partnerId}/impersonate`, {},
      { headers: { Authorization: `Bearer ${token}` } });
    localStorage.setItem('expedify_admin_token', token);
    return adoptSession(response.data);
  };

  const endSupportSession = () => {
    const adminToken = localStorage.getItem('expedify_admin_token');
    localStorage.removeItem('expedify_admin_token');
    if (!adminToken) {
      // Nothing to go back to — the admin token was cleared or never stored. Signing out is
      // the honest outcome; silently staying in the partner's account is not.
      logout();
      return;
    }
    localStorage.setItem('expedify_token', adminToken);
    setToken(adminToken);
    setUser(null);   // refetched by the effect on `token`, as the admin
  };

  const logout = () => {
    localStorage.removeItem('expedify_token');
    localStorage.removeItem('expedify_admin_token');
    setToken(null);
    setUser(null);
  };

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${token}`
  });

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      token,
      login,
      register,
      adoptSession,
      startSupportSession,
      endSupportSession,
      // True only while an admin is signed in as somebody else.
      impersonating: !!user?.impersonated_by,
      logout,
      getAuthHeaders,
      refreshUser: fetchUser,
      API_URL
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
